// MATERIAL UI IMPORTS
import { MenuItem, SelectChangeEvent, Grid } from "@mui/material";

import {
  StyledAvatar,
  StyledReviewTitle,
  StyledSecondaryFormLabel,
  StyledSelectContainer,
} from "../../common/styles/CryptoWithdrawalStyled";

// HOOKS
import { useIsMobile } from "src/utils/hooks/useIsMobile";

// ENUMS
export enum SelectType {
  DEFAULT = "DEFAULT",
  IMAGE = "IMAGE",
}

// INTERFACES
interface IFormOptions {
  value: string | undefined;
  displayName?: string;
  image?: string;
  memo?: string;
}

interface IFormSelect {
  type?: SelectType;
  title?: string;
  labelId: string;
  id: string;
  value: string;
  placeholder: string;
  onChange: (event: SelectChangeEvent<any>) => void;
  options: IFormOptions[];
  fullWidth?: boolean;
  disabled?: boolean;
}

/**
 * A Form Select component that can be used to render a select input field with options.
 *
 * @function
 * @param {SelectType} type - The type of select input field to render.
 * @param {string} title - The title to display as the label for the select input field.
 * @param {string} labelId - The id of the label element.
 * @param {string} id - The id of the select input field.
 * @param {string} value - The value of the select input field.
 * @param {string} placeholder - The placeholder text to display in the select input field.
 * @param {function} onChange - The function to call when the select input field value changes.
 * @param {IFormOptions[]} options - The options to render in the select input field.
 * @param {boolean} fullWidth - Whether the select input field should take up the full width of its container.
 * @param {boolean} disabled - Whether the select input field is disabled.

 * @returns {JSX.Element} The JSX code to render the Form Select component.
 */
export const FormSelect = ({
  type,
  title,
  labelId,
  id,
  value,
  placeholder,
  onChange,
  options,
  fullWidth,
  disabled = false,
}: IFormSelect): JSX.Element => {
  const isMobile = useIsMobile();

  const renderValue = (selectedValue: string) => {
    if (!selectedValue) {
      return <StyledSecondaryFormLabel>{placeholder}</StyledSecondaryFormLabel>;
    }

    const selectedOption = options.find((option) => option.value === selectedValue);
    if (!selectedOption) return <StyledSecondaryFormLabel>{placeholder}</StyledSecondaryFormLabel>;

    const maxLength = isMobile ? 25 : 40; // Maximum length before truncation

    const truncateMiddle = (value: string, maxLength: number) => {
      if (value.length <= maxLength) return value;

      const startLength = 6;
      const endLength = 6;

      const start = value.substring(0, startLength); // Start part of the string
      const end = value.substring(value.length - endLength); // End part of the string

      return `${start}...${end}`;
    };

    const truncatedValue = truncateMiddle(
      selectedOption.displayName ?? selectedOption.value ?? "",
      maxLength,
    );

    if (type === SelectType.IMAGE && selectedOption.image) {
      return (
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <StyledAvatar src={selectedOption.image} alt={truncatedValue} />
          </Grid>
          <Grid item>{truncatedValue}</Grid>
        </Grid>
      );
    }

    return truncatedValue;
  };

  // Custom onChange handler to include displayName
  const handleChange = (e: SelectChangeEvent<any>) => {
    const selectedValue = e.target.value as string;

    // Find the selected option to get its displayName
    const selectedOption = options.find((opt) => opt.value === selectedValue);

    // Create a new event object with the displayName included
    const enhancedEvent = {
      ...e,
      target: {
        ...e.target,
        value: selectedValue,
        name: e.target.name,
        displayName: selectedOption?.displayName || selectedValue,
        memo: selectedOption?.memo,
      },
    };

    // Call the original onChange with our enhanced event and the full option
    onChange(enhancedEvent as SelectChangeEvent<any>);
  };

  if (type === SelectType.IMAGE) {
    const renderAvatarItem = (option: IFormOptions) => (
      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <StyledAvatar src={option.image} alt={option.value} />
        </Grid>
        <Grid item>{option.value}</Grid>
      </Grid>
    );

    return (
      <>
        {title && <StyledReviewTitle>{title}</StyledReviewTitle>}
        <StyledSelectContainer
          labelId={labelId}
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          displayEmpty
          renderValue={() => renderValue(value)}>
          {options.length > 0 && options[0].value ? (
            options.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {renderAvatarItem(option)}
              </MenuItem>
            ))
          ) : (
            <MenuItem value={"No option available"}>
              {title === "Select Address"
                ? "No address available. Click to add a new one"
                : "No options available."}
            </MenuItem>
          )}
        </StyledSelectContainer>
      </>
    );
  }

  return (
    <>
      {title && <StyledReviewTitle>{title}</StyledReviewTitle>}
      {
        <StyledSelectContainer
          labelId={labelId}
          id={id}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          displayEmpty
          renderValue={() => renderValue(value)}>
          {options.length > 0 && options[0].value ? (
            options.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.displayName ?? option.value}
              </MenuItem>
            ))
          ) : (
            <MenuItem value={"No option available"}>
              {title === "Select Address"
                ? "No address available. Click to add a new one"
                : "No options available."}
            </MenuItem>
          )}
        </StyledSelectContainer>
      }
    </>
  );
};
